// @givmo/app-admin — the company gift-card notice a surface shows, resolved while it is open (GC-FU12b).
//
// Resolves the signed notice for a funding source (`gift-disclaimer.ts`) and answers what to render:
// the resolved notice once it is in, its floor (the same signed bytes) before that — so the panel never
// renders empty and never flashes another body. One query per notice: switching the funding source
// switches the notice, never the bytes under one.

import { useQuery } from '@tanstack/react-query';
import type { ResolvedDisclosure } from '@givmo/disclosure';
import {
  companyGiftCardNoticeFloor,
  resolveCompanyGiftCardNotice,
  type CompanyGiftCardNotice,
} from './gift-disclaimer';

/** The query key for a notice's resolve: one entry per surface and payer. */
export function companyGiftCardNoticeQueryKey(notice: CompanyGiftCardNotice): readonly unknown[] {
  return ['company-gift-card-notice', notice.surface, notice.payer];
}

/** The notice to render: resolved while `enabled`, its floor until the resolve answers. */
export function useCompanyGiftCardNotice(
  notice: CompanyGiftCardNotice,
  enabled: boolean,
): ResolvedDisclosure {
  const query = useQuery({
    queryKey: companyGiftCardNoticeQueryKey(notice),
    queryFn: () => resolveCompanyGiftCardNotice(notice),
    enabled,
    retry: false,
  });
  return query.data ?? companyGiftCardNoticeFloor(notice);
}
